import type { DiscussionThread } from "@/types";

export const TAG_BG_COLORS: Record<string, string> = {
  diseño: "bg-pink-100 dark:bg-pink-900/40",
  producto: "bg-purple-100 dark:bg-purple-900/40",
  ingeniería: "bg-indigo-100 dark:bg-indigo-900/40",
  ux: "bg-rose-100 dark:bg-rose-900/40",
  estrategia: "bg-amber-100 dark:bg-amber-900/40",
  datos: "bg-cyan-100 dark:bg-cyan-900/40",
  cultura: "bg-emerald-100 dark:bg-emerald-900/40",
  growth: "bg-green-100 dark:bg-green-900/40",
  marketing: "bg-orange-100 dark:bg-orange-900/40",
  dev: "bg-blue-100 dark:bg-blue-900/40",
  react: "bg-sky-100 dark:bg-sky-900/40",
  frontend: "bg-teal-100 dark:bg-teal-900/40",
  salud: "bg-red-100 dark:bg-red-900/40",
  remoto: "bg-lime-100 dark:bg-lime-900/40",
  ventas: "bg-violet-100 dark:bg-violet-900/40",
  soporte: "bg-fuchsia-100 dark:bg-fuchsia-900/40",
  roadmap: "bg-yellow-100 dark:bg-yellow-900/40",
};

export const TAG_SECTION_COLORS: Record<string, string> = {
  diseño: "text-pink-600 dark:text-pink-400",
  producto: "text-purple-600 dark:text-purple-400",
  ingeniería: "text-indigo-600 dark:text-indigo-400",
  ux: "text-rose-600 dark:text-rose-400",
  estrategia: "text-amber-600 dark:text-amber-400",
  datos: "text-cyan-600 dark:text-cyan-400",
  cultura: "text-emerald-600 dark:text-emerald-400",
  growth: "text-green-600 dark:text-green-400",
  marketing: "text-orange-600 dark:text-orange-400",
  dev: "text-blue-600 dark:text-blue-400",
  react: "text-sky-600 dark:text-sky-400",
  frontend: "text-teal-600 dark:text-teal-400",
  salud: "text-red-600 dark:text-red-400",
  remoto: "text-lime-600 dark:text-lime-400",
  ventas: "text-violet-600 dark:text-violet-400",
  soporte: "text-fuchsia-600 dark:text-fuchsia-400",
  roadmap: "text-yellow-600 dark:text-yellow-400",
};

export const EMOJI_OPTIONS = [
  "💬", "🔥", "⚡", "🎯", "🚀", "💡", "📌", "🎨",
  "⚙️", "📊", "🔒", "📢", "🤝", "💰", "📋", "🧪",
  "🐛", "✨", "📦", "🔔", "❤️", "🌟", "🎉", "📝",
  "🛠️", "🌍", "🧩", "📈", "🔍", "🏷️", "💎", "🎭",
];

export function getEmojiCircleBg(thread: DiscussionThread): string {
  const firstTag = thread.tags[0]?.toLowerCase();
  if (firstTag && TAG_BG_COLORS[firstTag]) return TAG_BG_COLORS[firstTag];
  return "bg-slate-100 dark:bg-slate-800/60";
}

export function formatRelativeTime(value: string): string {
  const now = Date.now();
  const date = new Date(value).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHrs = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHrs < 24) return `${diffHrs}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return new Date(value).toLocaleDateString("es-AR", { day: "numeric", month: "short" });
}

export interface TagGroup {
  label: string;
  tag: string | null;
  threads: DiscussionThread[];
}

export function groupThreadsByTag(threads: DiscussionThread[]): TagGroup[] {
  const tagMap = new Map<string, DiscussionThread[]>();
  const noTag: DiscussionThread[] = [];

  for (const thread of threads) {
    if (thread.tags.length === 0) {
      noTag.push(thread);
    } else {
      const primaryTag = thread.tags[0].toLowerCase();
      if (!tagMap.has(primaryTag)) tagMap.set(primaryTag, []);
      tagMap.get(primaryTag)!.push(thread);
    }
  }

  const groups: TagGroup[] = [];

  if (noTag.length > 0) {
    groups.push({ label: "General", tag: null, threads: noTag });
  }

  for (const [tag, tagThreads] of tagMap) {
    groups.push({
      label: tag.charAt(0).toUpperCase() + tag.slice(1),
      tag,
      threads: tagThreads,
    });
  }

  return groups;
}


const USER_AVATAR_PALETTES = [
  { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/50', text: 'text-emerald-700 dark:text-emerald-300' },
  { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-300' },
  { bg: 'bg-violet-100 dark:bg-violet-900/50', text: 'text-violet-700 dark:text-violet-300' },
  { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-700 dark:text-rose-300' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/50', text: 'text-cyan-700 dark:text-cyan-300' },
  { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-700 dark:text-orange-300' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/50', text: 'text-indigo-700 dark:text-indigo-300' },
  { bg: 'bg-pink-100 dark:bg-pink-900/50', text: 'text-pink-700 dark:text-pink-300' },
  { bg: 'bg-teal-100 dark:bg-teal-900/50', text: 'text-teal-700 dark:text-teal-300' },
] as const;

export function getUserAvatarColor(userId: number): { bg: string; text: string } {
  const index = Math.abs(userId) % USER_AVATAR_PALETTES.length;
  return USER_AVATAR_PALETTES[index];
}